import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { ChildTicketPlanView } from '../components/features/child-ticket-plan/index.js';
import { useProjectDetail } from '../hooks/useProjects.js';
import { useAuth } from '../hooks/index.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細ページの「基本設計」タブ。Backlog へ展開する子票群を表で組み立てる画面をマウントし、見出し・チャンク名・担当者の既定値に使う母票の情報を渡す
 * @graph-connects ChildTicketPlanView [calls] 子票展開エディタ描画
 * @graph-connects useProjectDetail [calls] 母票の案件名・開発リーダー・担当者の取得
 */
export const Route = createFileRoute('/projects/$pjKey/basic-design')({
  component: ProjectBasicDesignTab,
});

/**
 * 母票の取得が落ち着くまでシートを組まない。
 *
 * 初期 13 行は**作る瞬間の母票の情報**で担当者を埋めるため、取得前に組むと開発リーダーが
 * 空のまま固定され、あとからデータが届いても入らない（行は一度しか作られない）。
 * @graph-connects ChildTicketPlanView [calls] 子票展開エディタ描画
 * @graph-connects useProjectDetail [calls] 母票の案件名・開発リーダー・担当者の取得
 * @graph-connects useAuth [calls] 展開を押した人の判定
 */
function ProjectBasicDesignTab(): ReactNode {
  const { pjKey } = Route.useParams();
  const projectQuery = useProjectDetail(pjKey);
  const { user } = useAuth();

  if (projectQuery.isPending) {
    return (
      <div className="animate-content-reveal space-y-3">
        <Skeleton variant="rectangular" height={36} />
        <Skeleton variant="rectangular" height={520} />
      </div>
    );
  }

  const project = projectQuery.data;
  // 開発リーダーかどうかは email で突き合わせる。表示名（nickname）で比べると
  // 同姓同名や表記ゆれで取り違えるため、識別子の方で判定する。
  const devLeaderEmail = project?.devLeaderEmail ?? null;
  return (
    <ChildTicketPlanView
      pjKey={pjKey}
      caseTitle={project?.summary ?? pjKey}
      // DLE は開発リーダーとは別のカスタムフィールドで、入っている人も違う。
      // 母票の詳細では引けないため、展開 API 側が専用の解決で埋める。
      people={{
        devLeader: project?.devLeader ?? null,
        dle: null,
        pl: project?.assignee ?? null,
      }}
      isDevLeader={devLeaderEmail !== null && user?.email != null && devLeaderEmail === user.email}
    />
  );
}
